import { onBeforeUnmount, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { isArray, isRecord, isString, typeGuardGenerator } from 'tcheck';

import { useRootStore } from '../stores/root';

import {
  constructCognitoStorageKey,
  extractJWTData,
} from '../helpers/token_extraction';

export function getMobileCallHandler() {
  return window?.flutter_inappwebview?.callHandler;
}

export function isOnMobileApp() {
  return !!getMobileCallHandler();
}

export async function sendMessageToApp(
  functionName: string,
  data?: Record<string, unknown>,
) {
  const callHandler = getMobileCallHandler();

  if (!callHandler) {
    return;
  }

  const dataPayload = data ?? {};

  // We stringify all the values in the data payload
  const payloadToSend: Record<string, string> = {};
  Object.entries(dataPayload).forEach(([key, value]) => {
    if (isRecord(value) || isArray(value)) {
      payloadToSend[key] = JSON.stringify(value);
    } else {
      payloadToSend[key] = `${value}`;
    }
  });

  // We generate a new, random meta handler ID.
  const handlerId = 'meta_handler_' + Math.floor(Math.random() * 100000);

  // We manually create a new Promise object. This promise is how the web
  // app will react to any response the mobile app sends.

  return new Promise((resolve) => {
    // Create a meta object with the ID above and append it to the head element.
    const meta = document.createElement('meta');
    meta.id = handlerId;

    const head = document.querySelector('head');
    if (!head) {
      throw new Error('Head element not found');
    }
    head.appendChild(meta);

    // We're adding an event listener to the meta object with a custom event.
    // The idea is that we pass the random element id to the application so that
    // when the mobile application sends a response, it can respond to the specific
    // meta ID we set up. This event listener will resolve this promise and
    // let the web application continue in response to a sepecific query.
    meta.addEventListener('QsrMobileResponse', ((
      ev: CustomEvent<{ payload: { response: unknown } }>,
    ) => {
      resolve(ev.detail.payload.response);
    }) as EventListener);

    // We package up the data that the web app is sending to the mobile application
    // and post it to the mobile application. We stringify then parse to make sure it's
    // only JSON compatible data being sent.
    const message = JSON.parse(
      JSON.stringify({
        handlerId,
        functionName,
        payload: payloadToSend,
      }),
    );

    callHandler('QsrMessenger', message);
  }).catch((err) => {
    const meta = document.getElementById(handlerId);
    meta?.parentNode?.removeChild(meta);
    throw err;
  });
}

/**
 * Mobile Application Login Handler. This function will receive a Custom Event from
 * the mobile application. The event will receive, parse and check all the detail
 * data. We should be receiving an id, access and refresh token from the mobile
 * application. We extract bits of data to form the Amplify local storage keys,
 * then save the tokens to local storage.
 */
export function mobileLoginHandler(ev: CustomEvent) {
  // Parsing event data
  if (!isRecord(ev)) {
    console.error('Validation Error ev is not an object');
    return;
  }
  const { detail } = ev;

  if (!isRecord(detail)) {
    console.error('Validation Error detail is not an object');
    return;
  }

  const { cognitoIdToken, cognitoAccessToken, cognitoRefreshToken } = detail;

  // parsing token data
  if (!isString(cognitoIdToken) || cognitoIdToken.length === 0) {
    console.error(
      'Validation Error cognitoIdToken is not a string or is empty',
    );
    return;
  }

  if (!isString(cognitoAccessToken) || cognitoAccessToken.length === 0) {
    console.error(
      'Validation Error cognitoAccessToken is not a string or is empty',
    );
    return;
  }

  if (!isString(cognitoRefreshToken) || cognitoRefreshToken.length === 0) {
    console.error(
      'Validation Error cognitoRefreshToken is not a string or is empty',
    );
    return;
  }

  setAuthTokensToLocalStorage({
    accessToken: cognitoAccessToken,
    idToken: cognitoIdToken,
    refreshToken: cognitoRefreshToken,
  });
}

interface MobileAppResponse {
  accessToken: string;
  idToken: string;
  refreshToken: string;
}

const isMobileAppResponse = typeGuardGenerator<MobileAppResponse>({
  accessToken: isString,
  idToken: isString,
  refreshToken: isString,
});

export async function requestAuthTokensFromMobile() {
  // console.log('Requesting Auth Tokens from Mobile');
  const result = await sendMessageToApp?.('appLoaded', {});
  // parse the credentials
  // Set the credentials to local storage

  if (!isMobileAppResponse(result)) {
    // console.log('Not a valid mobile app response', result);
    return;
  }

  setAuthTokensToLocalStorage({
    accessToken: result.accessToken,
    idToken: result.idToken,
    refreshToken: result.refreshToken,
  });
}

interface TokenSetPayload {
  accessToken: string;
  idToken: string;
  refreshToken: string;
}

// Separate function so that Magic Link can also use it
export function setAuthTokensToLocalStorage(payload: TokenSetPayload) {
  const { accessToken, idToken, refreshToken } = payload;

  // Extract the data from the JWT.
  const accessTokenDat = extractJWTData(accessToken);
  const idTokenDat = extractJWTData(idToken);

  // Construct the local storage keys
  const { cognitoPrefix, userPrefix } = constructCognitoStorageKey(
    accessTokenDat,
    idTokenDat,
  );

  const UserAttributes = [];

  if ('custom:authorName' in idTokenDat) {
    UserAttributes.push({
      Name: 'custom:authorName',
      Value: idTokenDat['custom:authorName'],
    });
  }

  UserAttributes.push({ Name: 'sub', Value: idTokenDat.email });
  UserAttributes.push({ Name: 'email', Value: idTokenDat.email });

  const userData = {
    UserAttributes,
    Username: idTokenDat.email,
  };

  // This is where we actually insert all of the data into the local storage
  localStorage.setItem(`${userPrefix}accessToken`, accessToken);
  localStorage.setItem(`${userPrefix}idToken`, idToken);
  localStorage.setItem(`${userPrefix}refreshToken`, refreshToken);

  localStorage.setItem(`${userPrefix}clockDrift`, '0');
  localStorage.setItem(`${userPrefix}userData`, JSON.stringify(userData));
  localStorage.setItem(`${cognitoPrefix}LastAuthUser`, `${idTokenDat.email}`);
}

async function handleMobileLogin(ev: CustomEvent) {
  const store = useRootStore();
  const { onMobileApp, user, mobilePlatform } = storeToRefs(store);

  if (ev.detail && ev.detail.platform) {
    mobilePlatform.value = ev.detail.platform;
  }

  onMobileApp.value = true;
  try {
    mobileLoginHandler(ev);
  } catch (error) {
    console.error('failed to handle mobile login', error);
  }
  // only refresh if user is not logged in
  if (!user.value && !localStorage.getItem('onMobileApp')) {
    // console.log('We Are RELOADING!');
    localStorage.setItem('onMobileApp', 'true');
    // window.location.reload();
    setTimeout(() => window.location.reload(), 1);
  }
}

export function useMobileLogin() {
  // @ts-expect-error: QsrMobileLoaded is a custom event from the mobile app
  window.addEventListener('QsrMobileLoaded', handleMobileLogin);
  // console.log('Mobile App Listener Added');

  onMounted(() => {
    window.sendMessageToApp?.('appLoaded', {});
  });

  onBeforeUnmount(() => {
    // @ts-expect-error: QsrMobileLoaded is a custom event from the mobile app
    window.removeEventListener('QsrMobileLoaded', handleMobileLogin);
    // console.log('Mobile App Listener removed');
  });
}
